import {
  Directive,
  effect,
  inject,
  input,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { ACCESS_LEVEL, AccessLevel, Section } from '@anessa/shared-models';
import { PermissionService } from '../services/permissions/permission.service';

/**
 * Structural directive: render the element only when the current user has at
 * least `level` (default `view`) Tier-1 access to `section`.
 *
 *   <a *appHasSection="'approvisionnements'">…</a>              // needs view
 *   <button *appHasSection="'stocks'; level: 'edit'">…</button> // needs edit
 */
@Directive({
  selector: '[appHasSection]',
})
export class HasSectionDirective {
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly permissionService = inject(PermissionService);

  readonly section = input.required<Section>({ alias: 'appHasSection' });
  readonly level = input<AccessLevel>(ACCESS_LEVEL.VIEW, {
    alias: 'appHasSectionLevel',
  });

  private isVisible = false;

  constructor() {
    effect(() => {
      const section = this.section();
      const level = this.level();
      // Track lastResolvedPlantId so this effect re-runs once the access cache
      // is populated after an async load.
      this.permissionService.lastResolvedPlantId();
      const canShow = this.meets(section, level);

      if (canShow && !this.isVisible) {
        this.viewContainerRef.createEmbeddedView(this.templateRef);
        this.isVisible = true;
        return;
      }

      if (!canShow && this.isVisible) {
        this.viewContainerRef.clear();
        this.isVisible = false;
      }
    });
  }

  private meets(section: Section, level: AccessLevel): boolean {
    switch (level) {
      case ACCESS_LEVEL.ADMIN:
        return this.permissionService.canAdminSection(section);
      case ACCESS_LEVEL.EDIT:
        return this.permissionService.canEditSection(section);
      default:
        return this.permissionService.canViewSection(section);
    }
  }
}
